import { makeAutoObservable, runInAction } from 'mobx';
import { PlaylistItem } from '~/services/scheduler/types';
import { triggerStore } from './trigger.store';
import { playbackStore } from './playback.store';
import { logger } from '~/services/logging/logger';
// NÃO importar `tizenAvplay` aqui: o corpo do módulo dele avalia em TODA plataforma e o puxaria
// pro bundle mesmo sem `VITE_AVPLAY_BACKEND=true`, quebrando o tree-shaking em que a validação de
// build se apoia. O `ramReload` lê os contadores do localStorage, que é o bridge já usado.
import { maybeReloadAtLoopWrap } from '~/services/avplay/ramReload';
import { detectLoopPattern } from '~/services/playback/loopPattern';
import { resolveLoopTarget, paintLeadMs } from '~/services/playback/loopSync';
import { serverNow } from '~/services/time/serverClock';
// Detecção de plataforma vem da abstração — a store NUNCA olha `window.tizen`/
// `window.webOS` direto (vide CLAUDE.md, Platform Decoupling). Importado do módulo
// FOLHA, não do barrel `~/services/platform`: o barrel reexporta o PlatformContext
// (React) e todas as implementações, e arrastá-las pro grafo da store repetiria o
// problema que o aviso do `tizenAvplay` (topo deste arquivo) já documenta.
import { detectPlatform } from '~/services/platform/utils/detectPlatform';
import { markBoot } from '~/utils/bootTiming';
import { FailureBrake } from '~/services/playback/failureBrake';

/**
 * Unified media item type for stream playback.
 */
export interface StreamMediaItem {
  id: string;
  type: 'video' | 'audio' | 'image' | 'stream';
  name: string;
  url: string;
  streamUrl?: string;
  startOffsetSeconds?: number;
  duration?: number;
  /**
   * True iff the operator explicitly set a duration override on the campanha
   * conteudo association. When false, `duration` is the natural content
   * duration and the player MUST NOT cut the video short — let it reach
   * its natural onEnded event.
   */
  hasDurationOverride?: boolean;
  mimeType?: string;
  campanhaId?: number; // Campaign ID from PlaylistItem
  playbackStartedAt?: string; // Canonical start timestamp for synchronized playback
  elementos?: import('./interactiveElements.store').ElementSyncDto[];
  /**
   * Multi-zone — ID of the zone this content item is assigned to.
   * When the layout has zones, the Stream scene filters items per zone.
   * Undefined = content plays in any zone (fallback behavior).
   */
  zonaId?: number;
}

/**
 * Converts a PlaylistItem to StreamMediaItem.
 */
/**
 * Índice em que a reprodução deve COMEÇAR.
 *
 * <p>Com sync de videowall, começar sempre no item 0 fazia o player piscar o primeiro
 * conteúdo da campanha a cada boot/reload: ele renderizava o item 0, só então calculava
 * a posição do grupo e pulava. Resolvendo aqui, antes do primeiro render, o primeiro
 * frame já é o item certo — não há o que piscar.</p>
 *
 * <p>Só age quando o chamador NÃO pediu um índice específico ({@code startIndex === 0}):
 * restore de trigger e retomada de slot passam índice próprio e têm precedência.</p>
 */
function resolveInitialIndex(
  items: Array<{ playbackStartedAt?: string; duration?: number }>,
  startIndex: number,
): number {
  const teto = Math.max(0, items.length - 1);
  const pedido = Math.min(startIndex, teto);
  if (startIndex !== 0 || items.length === 0) return pedido;

  const epoca = items.find((it) => it.playbackStartedAt)?.playbackStartedAt;
  if (!epoca) return pedido;

  const alvo = resolveLoopTarget({
    playbackStartedAt: epoca,
    durations: items.map((it) => it.duration || 0),
    nowMs: serverNow() + paintLeadMs(detectPlatform()),
  });
  if (!alvo) return pedido;

  console.log(
    `[StreamStore] 🎯 Sync: começando no item ${alvo.index} (loopPos ${alvo.loopPosition.toFixed(1)}s) em vez do 0`,
  );
  return Math.min(alvo.index, teto);
}

function fromPlaylistItem(item: PlaylistItem): StreamMediaItem {
  return {
    id: item.id,
    type: item.type,
    name: item.name,
    url: item.url,
    duration: item.duration,
    hasDurationOverride: item.hasDurationOverride,
    mimeType: item.mimeType,
    campanhaId: item.campanhaId,
    playbackStartedAt: item.playbackStartedAt,
    elementos: item.elementos,
    zonaId: item.zonaId,
    streamUrl: item.streamUrl,
    startOffsetSeconds: item.startOffsetSeconds,
  };
}

/**
 * O fim de mídia recebido é OBSOLETO (pertence a uma playlist que já passou)? — decisão pura.
 *
 * <p>POR QUE A GUARDA EXISTE. Quando a playlist é substituída, o vídeo antigo ainda pode disparar
 * `onEnded`/`onstreamcompleted` depois. Avançar com esse evento pularia um conteúdo da playlist
 * NOVA. Descartar é correto.
 *
 * <p>🔴 O DEADLOCK QUE ISTO CONSERTA (reproduzido e medido na bancada SSSP QB50B, 2026-07-27). A
 * guarda antiga comparava só o `playlistVersion`, e o ref com a versão do evento só é atualizado
 * nos handlers de INÍCIO de reprodução. Duas trocas de playlist em rajada (ex.: reordenar o loop
 * 2× seguidas no admin) bombam o `playlistVersion` **enquanto o vídeo já está tocando** → o ref
 * fica velho → quando aquele vídeo termina, o fim é descartado como obsoleto → NÃO avança → a
 * mídia não muda → nenhum handler de início roda → o ref NUNCA se atualiza → **todo fim futuro é
 * descartado, para sempre**.
 *
 * <p>ASSINATURA OBSERVADA (via CDP, durante o congelamento): player AVPlay VIVO em
 * `state=PLAYING` parado a 41ms do fim (`currentMs 14917 / durationMs 14958`), nenhum `open` novo,
 * heap plano, RAM saudável, JS respondendo e **heartbeat FRESCO** — ou seja o admin mostra o
 * player ONLINE com a tela congelada no último frame. Não sai sozinho: só com intervenção manual
 * na programação. Congelamentos de 6 min foram medidos; em loja seria indefinido.
 *
 * <p>CRITÉRIO CORRETO: a IDENTIDADE DA MÍDIA, não a versão da playlist. O fim é obsoleto se a
 * mídia que terminou não é mais a atual. Se ela sobreviveu à troca de playlist, o fim é legítimo —
 * não importa quantas vezes a playlist foi substituída no meio. O `playlistVersion` segue como
 * fallback para chamadores que não informam a mídia.
 *
 * <p>Impacto em contagem de exibições: NENHUM. Não mexe em índice nem reseta loop — só deixa de
 * descartar um fim válido.
 */
export function isStaleMediaEnd(p: {
  eventVersion?: number;
  currentVersion: number;
  endedMediaId?: string;
  currentMediaId: string | null;
}): boolean {
  // Caminho autoritativo: sabemos QUAL mídia terminou.
  if (p.endedMediaId !== undefined) {
    if (p.currentMediaId == null) return false; // sem mídia atual → deixa avançar (recupera)
    return p.endedMediaId !== p.currentMediaId;
  }
  // Fallback (chamador legado, sem mediaId): compara versão.
  if (p.eventVersion === undefined) return false;
  return p.eventVersion !== p.currentVersion;
}

class StreamStore {
  playlist: StreamMediaItem[] = [];
  currentIndex: number = 0;
  isPlaying: boolean = false;
  isLoading: boolean = true;
  error: string | null = null;
  /**
   * Embedded (§6/D2): o app host escondeu a EffluxView → congela o playback
   * (pausa o `<video>`, para de avançar). Default `false` → ZERO efeito em
   * tizen/webos/web/android (só o comando `hide`/`stop` do canal embedded seta).
   * O Stream tem um efeito que pausa e SEGURA pausado enquanto true (pega o
   * advance-watchdog que re-playa vídeo pausado). `show`/`start` volta pra false.
   */
  isHostHidden: boolean = false;
  /**
   * Janela (ms) em que um setPlaylist subsequente preserva o currentIndex
   * se os items são iguais. Protege contra reset acidental do currentIndex
   * causado por sync que chega ~50ms após restorePlaylist (cenário Tier 4
   * onde playbackStore.currentSlotIndex sempre é 0 e não pode ajudar).
   * Ver setPlaylist + restorePlaylist abaixo.
   */
  private static readonly POST_RESTORE_GUARD_MS = 10_000;
  private lastRestoreAt: number = 0;
  /**
   * Inicia muted em browser/web (políticas de autoplay do Chromium exigem),
   * mas em Android (Capacitor WebView) o MainActivity configura
   * {@code setMediaPlaybackRequiresUserGesture(false)} — então autoplay com
   * som é permitido. Em retail media com volume gerenciado pelo server, NÃO
   * queremos o overlay "Mudo - Pressione OK" aparecendo pra cliente final.
   *
   * TIZEN + AVPlay: também NÃO muta por padrão. O vídeo toca no plano HW do
   * AVPlay (não é `<video>` do Chromium → sem política de autoplay), e o mute do
   * app é espelhado no MUTE DE SISTEMA — o que faz o Samsung piscar o OSD de mudo
   * enquanto toca vídeo com áudio. Deixando desmutado, cada conteúdo toca o áudio
   * que TEM (uns com som, outros mudos naturalmente) e o sistema nunca é mutado →
   * sem OSD. Quem quiser silenciar usa o "Pressione OK" (toggleMute). Browser/webOS
   * e Tizen SEM AVPlay seguem mutados por padrão (autoplay do `<video>`).
   */
  isMuted: boolean = (() => {
    try {
      const w = window as unknown as {
        __effluxPlatform?: { platform: string };
        tizen?: unknown;
        Capacitor?: { getPlatform?: () => string };
      };
      const platform = w.__effluxPlatform?.platform;
      // ⚠️ TIMING: este store é criado no IMPORT do módulo (`stores/index.ts`), ANTES do
      // PlatformContext setar `window.__effluxPlatform` (efeito React). No boot o `platform`
      // acima ainda é undefined → cairia no default muted. Por isso cada plataforma tem um
      // sinal disponível JÁ no import:
      //   • Tizen  → global `window.tizen` (injetado pelo runtime da TV antes do bundle).
      //   • Android → global `window.Capacitor` (injetado pelo bridge Capacitor antes do bundle).
      // 🔴 BUG (fix 2026-08-03): antes só o Android via `__effluxPlatform` (undefined no import)
      // era checado → o tablet caía no `return true` (mudo) e NUNCA soltava áudio de vídeo.
      // Android NATIVO tem `setMediaPlaybackRequiresUserGesture(false)` no WebView (MainActivity)
      // → autoplay COM áudio é permitido → deve começar DESMUTADO. (Browser mobile Android NÃO
      // tem `window.Capacitor` → cai no default muted, correto pra política de autoplay do <video>.)
      const capPlatform = w.Capacitor?.getPlatform?.();
      if (platform === 'android' || capPlatform === 'android') return false;
      const isTizen = platform === 'tizen' || typeof w.tizen !== 'undefined';
      const avplayBackend = import.meta.env.VITE_AVPLAY_BACKEND === 'true';
      if (isTizen && avplayBackend) return false;
      return true;
    } catch {
      return true;
    }
  })();
  activeCampaignId: string | null = null; // ID da campanha ativa (se usar Scheduler)
  isTriggerPlayback: boolean = false; // true when playing trigger content
  playlistVersion: number = 0; // Epoch counter — incremented on every playlist change
  /** Unix ms — when the current item started playing. Used by PlaybackStatusService
   *  to compute accurate elapsed for BOTH images and videos (video.currentTime
   *  alone misses image items and duration overrides shorter than the asset). */
  currentItemStartedAt: number = Date.now();

  /**
   * v1.0.143 — overlay de controle/debug (MediaInfo) visível? Elevado do estado
   * local do Stream pra o KioskSettingsButton (renderizado fora do Stream)
   * decidir se mostra o botão de Configurações DURANTE a mídia — só com o
   * painelzinho de controle aberto. Em telas iniciais o botão usa outro
   * critério (degraded), então este flag só importa durante a mídia.
   */
  controlsVisible: boolean = false;

  /** Freio da cascata de pulos — vide {@link handleMediaFailure}. */
  private failureBrake = new FailureBrake();
  private failureHold: {
    timer: ReturnType<typeof setTimeout>;
    mediaId: string;
    version: number;
  } | null = null;

  /**
   * Reentrada pelo relógio do grupo exclusivo — `SyncStore.realignToCanonicalClock`, injetado
   * no `SyncStore.init()` (mesmo padrão do `TriggerStore`: este arquivo não importa o SyncStore).
   */
  private realignHandler: ((source: string) => void) | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Vide {@link realignHandler}. */
  setRealignHandler(handler: ((source: string) => void) | null): void {
    this.realignHandler = handler;
  }

  setControlsVisible(visible: boolean) {
    this.controlsVisible = visible;
  }

  get currentMedia(): StreamMediaItem | null {
    if (this.playlist.length === 0) return null;
    return this.playlist[this.currentIndex] || null;
  }

  /**
   * Peek do próximo item da playlist sem avançar o índice.
   *
   * Usado pelo Stream component pra preload do próximo vídeo enquanto o
   * atual ainda está tocando — elimina o "Carregando vídeo..." de 1s na
   * transição entre mídias.
   *
   * Wraps around quando chega no final (loop infinito da playlist).
   * Retorna {@code null} se playlist vazia.
   *
   * Multi-zone: quando o item atual pertence a uma zona (zonaId != null),
   * pula irmãos da mesma campanha/zona pra que o preload aponte pro item
   * correto (o próximo que não faz parte do grupo de zonas atual).
   */
  get nextMedia(): StreamMediaItem | null {
    if (this.playlist.length === 0) return null;
    const current = this.playlist[this.currentIndex];
    let nextIdx = (this.currentIndex + 1) % this.playlist.length;
    if (current?.zonaId != null && current?.campanhaId != null) {
      let safety = 0;
      while (
        safety < this.playlist.length &&
        this.playlist[nextIdx]?.zonaId != null &&
        this.playlist[nextIdx]?.campanhaId === current.campanhaId
      ) {
        nextIdx = (nextIdx + 1) % this.playlist.length;
        safety++;
      }
    }
    return this.playlist[nextIdx] || null;
  }

  get hasNext(): boolean {
    return this.playlist.length > 0;
  }

  get progress(): { current: number; total: number } {
    return {
      current: this.currentIndex + 1,
      total: this.playlist.length,
    };
  }

  /**
   * Sets the playlist from SchedulerStore or TriggerStore.
   * Used when Scheduler determines which campaign's playlist to play,
   * or when TriggerStore interrupts with trigger content.
   *
   * @param items - PlaylistItem array from the active campaign or trigger
   * @param campaignId - Optional campaign/trigger ID for tracking
   * @param isTrigger - Whether this is trigger content (default: false)
   * @param startIndex - Optional starting index (used when restoring campaign)
   */
  setPlaylist(
    items: PlaylistItem[],
    campaignId?: string,
    isTrigger: boolean = false,
    startIndex: number = 0,
    forceUpdate: boolean = false,
  ): void {
    // Orçamento do preto: "o player já sabe O QUE tocar". Aqui em cima de propósito — vale
    // qualquer que seja o ramo tomado abaixo (idempotente, force, post-restore guard). Idempotente
    // por boot, então os syncs seguintes não mexem na marca. Vide bootTiming.
    if (items.length > 0) markBoot('playlist');
    // Helper: dois items são "iguais semanticamente" se id E todos os campos
    // que afetam a reprodução são iguais. Comparar APENAS id era bug —
    // mudanças de duration/durationOverride/url na campanha não chegavam
    // no player porque o idempotente descartava a playlist nova como
    // sameContent=true (Renato 2026-05-28).
    //
    // v1.0.58 (2026-05-28) — comparação de URL agora normaliza presigned
    // refresh.
    //
    // BUG observado por Renato em campo: o player ficava em loop 1→5→1
    // (toca 5 dos 8 vídeos e reseta pra início). Causa: cada sync com
    // servidor traz URLs com SIGNATURE diferente (R2 regenera
    // X-Amz-Signature a cada presignGetObject). Comparar `a.url === b.url`
    // dava FALSE mesmo pro MESMO arquivo, fazendo sameContent=false e
    // setPlaylist resetar o currentIndex pra startIndex.
    //
    // Fix: comparar só `origin + pathname` (ignora query string). Bate
    // com a mesma lógica já implementada em
    // media.store.ts:getLocalUrlAsync (linha ~437) pra detectar
    // presigned refresh sem invalidar o cache. Aqui aplicamos o mesmo
    // padrão pra não invalidar o playback state.
    const isSameUrlIgnoringSignature = (
      urlA: string,
      urlB: string,
    ): boolean => {
      if (urlA === urlB) return true;
      try {
        const a = new URL(urlA);
        const b = new URL(urlB);
        // Mesmo origin+pathname = mesmo objeto no R2/storage,
        // independente do signature query param.
        return a.origin === b.origin && a.pathname === b.pathname;
      } catch {
        // URLs malformadas — cai pro strict compare.
        return false;
      }
    };

    const isSameItem = (a: PlaylistItem, b?: StreamMediaItem): boolean => {
      if (!b) return false;
      return (
        a.id === b.id &&
        isSameUrlIgnoringSignature(a.url, b.url) &&
        a.duration === b.duration &&
        (a.hasDurationOverride ?? false) === (b.hasDurationOverride ?? false) &&
        // A ÉPOCA FAZ PARTE DA IDENTIDADE DA PLAYLIST.
        //
        // Sem isto, uma playlist que chega COM âncora era descartada como "igual" à que já
        // estava SEM âncora — o ramo idempotente volta cedo e NÃO troca os itens da store.
        // Como todo o realinhamento (índice inicial, seek no canplay, encurtar imagem, pulso
        // de deriva) só age quando o item tem `playbackStartedAt`, o player ficava com os
        // itens velhos, sem época, e TODAS as correções ficavam inertes: ele parava no slot 0
        // pra sempre. Medido no embedded entrando no grupo (2026-10-06): dezenas de
        // "Idempotent — preserving currentIndex 0" e nenhum "🎯 Sync: começando no item".
        //
        // Acontece sempre que a época aparece DEPOIS da playlist — que é exatamente o caso de
        // quem entra por último: o boot restaura do cache em disco (ainda sem época) e o
        // primeiro sync traz a época com o MESMO conteúdo.
        a.playbackStartedAt === b.playbackStartedAt
      );
    };

    // IDEMPOTENT BEHAVIOR: if the incoming playlist is identical to the current
    // one (same ids in same order) and it's not a trigger, preserve currentIndex
    // and currentItemStartedAt. This prevents the playback loop from resetting
    // to item 0 on every sync poll (which was the root cause of the "pulos
    // estranhos" seen by the admin Loop tab).
    const sameContent =
      !isTrigger &&
      this.playlist.length === items.length &&
      this.playlist.length > 0 &&
      items.every((it, i) => isSameItem(it, this.playlist[i])) &&
      this.isTriggerPlayback === false &&
      this.activeCampaignId === (campaignId ?? null);

    // POST-RESTORE GUARD: caso especial em que sameContent=false APENAS
    // porque o campaignId mudou (ex: 'Campanha' setado pelo restorePlaylist
    // → '1' vindo do schedulerStore via sync que chegou ~50ms depois).
    // Sem isso, o player perde o currentIndex que acabou de ser restaurado
    // após um trigger_deactivated. Bug Renato 2026-05-27 (Tier 4):
    // 1) trigger fired → save streamIndex=1
    // 2) library_updated SSE chega DURANTE trigger → sync inicia
    // 3) trigger_deactivated → restorePlaylist(items, 'Campanha', 1)
    // 4) sync (em flight) retorna → onScheduleChange → setPlaylist(items, '1')
    //    com startIndex=0 default
    // 5) sameContent=false (campaignId diferente) → currentIndex=0 = Prada ❌
    const sameItems =
      !isTrigger &&
      this.playlist.length === items.length &&
      this.playlist.length > 0 &&
      items.every((it, i) => isSameItem(it, this.playlist[i])) &&
      this.isTriggerPlayback === false;
    const recentlyRestored =
      this.lastRestoreAt > 0 &&
      Date.now() - this.lastRestoreAt < StreamStore.POST_RESTORE_GUARD_MS;
    const preserveAfterRestore = recentlyRestored && sameItems && !sameContent;

    console.log('[StreamStore] 🎬 setPlaylist called:', {
      itemsCount: items.length,
      campaignId: campaignId || 'none',
      isTrigger,
      startIndex,
      sameContent,
      preserveAfterRestore,
      currentIndexBefore: this.currentIndex,
    });

    if (sameContent && !forceUpdate) {
      console.log(
        '[StreamStore] ⏭️  Idempotent — same playlist, preserving currentIndex',
        this.currentIndex,
      );
      return;
    }

    if (sameContent && forceUpdate) {
      console.log(
        '[StreamStore] 🔄 Force update — same playlist, applying startIndex',
        startIndex,
      );
      runInAction(() => {
        // ÂNCORA VALE AQUI TAMBÉM. Este ramo cravava `startIndex` cru, então um
        // force-update (ex.: `player_orientation_changed` → forceResync com
        // resetPlaylistFromStart) jogava o player no slot 0 — de volta ao COMEÇO do
        // loop, fora de fase com o grupo, que é como o operador vê um peer "entrar
        // no início da playlist em vez de entrar junto". `resolveInitialIndex` já
        // aplica o mesmo clamp de antes e só desvia quando há época canônica e o
        // caller não pediu um índice específico — sem época, comportamento idêntico.
        this.currentIndex = resolveInitialIndex(items, startIndex);
        this.activeCampaignId = campaignId ?? null;
        this.currentItemStartedAt = Date.now();
      });
      return;
    }

    if (preserveAfterRestore) {
      console.log(
        '[StreamStore] 🛡️  Post-restore guard ativo — preservando currentIndex=' +
          this.currentIndex +
          ' (atualizando só campaignId)',
      );
      runInAction(() => {
        this.activeCampaignId = campaignId ?? null;
        this.isTriggerPlayback = false;
      });
      return;
    }

    runInAction(() => {
      this.playlistVersion++;
      this.playlist = items.map(fromPlaylistItem);
      this.currentIndex = resolveInitialIndex(items, startIndex);
      this.isLoading = false;
      this.activeCampaignId = campaignId ?? null;
      this.isTriggerPlayback = isTrigger;
      this.error = null;
      this.currentItemStartedAt = Date.now();
    });

    console.log('[StreamStore] ✅ Playlist updated:', {
      playlistLength: this.playlist.length,
      currentIndex: this.currentIndex,
      isTrigger: this.isTriggerPlayback,
      currentMedia: this.currentMedia
        ? {
            id: this.currentMedia.id,
            name: this.currentMedia.name,
            type: this.currentMedia.type,
          }
        : null,
      campaignId: this.activeCampaignId || 'none',
    });
  }

  /**
   * Set current index directly (used by TriggerStore for CONTINUOUS restart).
   */
  setCurrentIndex(index: number): void {
    runInAction(() => {
      this.currentIndex = index;
      this.currentItemStartedAt = Date.now();
    });
  }

  /**
   * Restore a previously saved playlist (StreamMediaItem[]) directly.
   * Used by TriggerStore to restore campaign state after trigger completes.
   */
  restorePlaylist(
    items: StreamMediaItem[],
    campaignId?: string,
    startIndex: number = 0,
  ): void {
    console.log('[StreamStore] 🔄 restorePlaylist called:', {
      itemsCount: items.length,
      campaignId: campaignId || 'none',
      startIndex,
    });

    runInAction(() => {
      this.playlistVersion++;
      this.playlist = [...items];
      // O boot offline-first passa por aqui. Fixar o índice 0 fazia o player abrir no
      // PRIMEIRO conteúdo e ficar fora do grupo — e o guard de 10s logo abaixo ainda
      // impedia o setPlaylist seguinte de corrigir. Com sync de videowall, a posição
      // certa já é conhecida no instante da restauração.
      this.currentIndex = resolveInitialIndex(items, startIndex);
      this.isLoading = false;
      this.activeCampaignId = campaignId ?? null;
      this.isTriggerPlayback = false;
      this.error = null;
      this.currentItemStartedAt = Date.now();
    });

    // Ativa guard contra reset do currentIndex por setPlaylist subsequente.
    // Cenário (Renato 2026-05-27): library_updated SSE dispara sync DURANTE
    // o trigger; resposta chega ~50ms depois do restorePlaylist, e o
    // onScheduleChange chama setPlaylist com campaignId='1' (diferente do
    // 'Campanha' setado aqui) → sameContent=false → reset pra currentIndex=0
    // (Prada). Esse guard preserva o currentIndex que acabou de ser
    // restaurado.
    this.lastRestoreAt = Date.now();

    console.log('[StreamStore] ✅ Playlist restored:', {
      playlistLength: this.playlist.length,
      currentIndex: this.currentIndex,
      lastRestoreAt: this.lastRestoreAt,
    });
  }

  playNext(): void {
    if (this.playlist.length === 0) return;

    let nextIndex = (this.currentIndex + 1) % this.playlist.length;

    // If playing trigger content and we've looped back to start
    if (this.isTriggerPlayback && nextIndex === 0) {
      console.log(
        '[StreamStore] 🎯 Trigger playlist reached end, notifying TriggerStore',
      );
      triggerStore.handleTriggerPlaylistComplete();
      // If handleTriggerPlaylistComplete changed the playlist (ONCE/RANDOM_ONE),
      // don't update currentIndex as the playlist was already replaced
      if (!this.isTriggerPlayback) return;
    }

    // Multi-zone group skip: when the current item belongs to a zone group
    // (zonaId != null), skip over all sibling items from the same campaign
    // (same campanhaId) that also belong to a zone. These are played
    // simultaneously by ZonePlayer, not sequentially.
    const current = this.playlist[this.currentIndex];
    if (current?.zonaId != null && current?.campanhaId != null) {
      let scanIdx = (this.currentIndex + 1) % this.playlist.length;
      let skipped = 0;
      while (
        skipped < this.playlist.length &&
        this.playlist[scanIdx]?.zonaId != null &&
        this.playlist[scanIdx]?.campanhaId === current.campanhaId
      ) {
        scanIdx = (scanIdx + 1) % this.playlist.length;
        skipped++;
      }
      nextIndex = scanIdx;
    }

    // RLD — ÚNICA âncora do refresh periódico (mitigação do leak nativo do AVPlay): o WRAP DO LOOP
    // (último item → primeiro), INDEPENDENTE do tipo (img ou vídeo) do último e do primeiro — o
    // `playNext` roda no fim de qualquer item. Ali a troca de conteúdo já é esperada pelo
    // espectador, então o preto curto do reload passa por "virou o anúncio". Quem decide SE
    // recarrega é o `maybeReloadAtLoopWrap` (tempo ≥ intervalo OU backstop de RAM); aqui só se
    // define ONDE ele tem permissão de agir.
    //
    // 🔴 WRAP = FIM DO PADRÃO, NÃO `nextIndex === 0` (medido no aparelho, 2026-07-28). A `playlist`
    // aqui é a sequência da HORA INTEIRA, não o padrão do loop: o backend gera UM padrão e o repete
    // N vezes na hora (R4 do loop-reproducao-player) — na bancada, 15 slots × 16 repetições. Com
    // `nextIndex === 0` a âncora só abre na virada da HORA, então um intervalo de 90min viraria
    // ~2h na prática, e um de 10min também viraria 1h. Medido na TV: os slotIndex subiam 6, 7,
    // 8 … 17 com `scheduledOffsetS` 141 → 292, sem nunca voltar a 0.
    //
    // O padrão vem de `detectLoopPattern` — o MESMO helper que alimenta o "Mídia: X/15 no padrão ·
    // Loop Y/16 da hora" do overlay de debug. Zero lógica nova.
    //
    // 🔴 NÃO mover pra `handleMediaEnd` nem pro fim de CADA vídeo num loop com SUB-padrão (p≥2): já
    // causou preto no fim de todo vídeo (regressão visível na loja). Por isso o wrap abaixo só dispara
    // no FIM DO PADRÃO — EXCETO quando o padrão TEM 1 slot (aí cada volta É o fim; ver abaixo).
    //
    // ✅ Vídeo ÚNICO: playlist de 1 VÍDEO loopa DENTRO do AVPlay (`finishOrLoop`), sem avançar
    // `currentIndex` → não passa por aqui. O recycle desse caso vive no `finishOrLoop`
    // (`AvplayVideo.tsx`). Este wrap cobre todo o resto (imagem única, N-idêntico, sub-padrão, alternada).
    //
    // 🔴 patternLength === 1 (LOOP DE 1 SLOT repetido N× pra encher a hora — imagem única, ou o MESMO
    // conteúdo em N slots): é o caso que travou a TV QB50B X5XN43EMPZNBD (crash 2026-08-30). O
    // slot_sequence tinha 360 slots da MESMA imagem → `detectLoopPattern` = 1 (todos os ids iguais),
    // mas `this.playlist.length` = 360. Com `wrapByPattern` exigindo `patternLength > 1` E
    // `wrapByFirstSlot` exigindo `patternLength >= length`, o loop de 1 slot caía no VÃO das duas → o
    // recycle NUNCA rodava → nem o gatilho de TEMPO nem o backstop de RAM podiam agir → o app rodou
    // ~72min (black-box: ageSec 4340, avOpens=0, nativeAvail min 125MB) → OOM/kill da OS → ~1h de tela
    // travada. FIX: `patternLength >= 1` faz o loop de 1 slot disparar o wrap a CADA volta
    // (`nextIndex % 1 === 0` é sempre true) — e o `maybeReloadAtLoopWrap` só recarrega 1×/intervalo
    // (o check por item é barato). Reciclar aqui respeita R1: o loop-base já completou → nenhuma
    // impressão cortada. (`length === 0` já deu return lá em cima, então aqui length ≥ 1.)
    if (this.playlist.length >= 1) {
      const patternLength = detectLoopPattern(
        this.playlist.map((m) => ({
          id: m.id,
          duration: m.duration ?? 10,
          campanhaId: m.campanhaId,
        })),
      ).patternLength;
      // Wrap NORMAL: detectLoopPattern achou um padrão (1 ≤ p < playlist inteira) → usa ele. Inclui
      // p===1 (loop de 1 slot: dispara a CADA volta, `nextIndex % 1 === 0`) E p≥2 (sub-padrão limpo;
      // cobre campanha com mínimo≥2, cujo 1º conteúdo repete DENTRO do padrão).
      const wrapByPattern =
        patternLength >= 1 &&
        patternLength < this.playlist.length &&
        nextIndex % patternLength === 0;
      // FALLBACK (Opção B, 2026-08-19): quando o detectLoopPattern DEGENERA — patternLength ===
      // playlist.length — o loop NÃO tem sub-padrão limpo. Típico da **playlist Alternada**: a
      // rotação de N itens no FIM de cada volta não divide a hora → o detector vê "1 padrão gigante
      // da hora" → o wrap dispararia ~1x/hora OU NUNCA → o recycle anti-leak nunca roda → o app
      // vaza/trava (crash em prod, TV Drogasil 2026-08-19). Nesse caso o começo do loop-base é
      // "voltar ao 1º slot" (mesmo content_id de playlist[0]). Reciclar AQUI respeita R1: o loop-base
      // anterior JÁ completou → NENHUMA impressão cortada. Vide loop-reproducao-player.md.
      const nextId = this.playlist[nextIndex]?.id;
      const wrapByFirstSlot =
        patternLength >= this.playlist.length &&
        nextId != null &&
        nextId === this.playlist[0]?.id;
      if (wrapByPattern || wrapByFirstSlot) {
        // 🔴 RETURN se o reload foi disparado — NÃO avançar o índice (medido em campo, 2026-07-28).
        //
        // Sem este return, o `playNext` seguia, o `currentIndex` ia pra 0 e o React PINTAVA o
        // primeiro conteúdo antes da página morrer → o operador via **verde → preto → verde** (com
        // o último item do loop vermelho e o primeiro verde, na SSSP).
        //
        // Pior que o visual: aquele slot fantasma emitia `slot_started` de um conteúdo que NUNCA
        // tocou, inflando a contagem de exibições do primeiro item do loop a cada reciclagem — o
        // oposto do que os mínimos contratuais precisam (R1).
        //
        // A página está indo embora de qualquer forma; quem reposiciona o loop é o boot.
        if (maybeReloadAtLoopWrap()) return;
      }
    }

    runInAction(() => {
      this.currentIndex = nextIndex;
      this.currentItemStartedAt = Date.now();
    });

    // v1.0.58 (2026-05-28) — sincroniza playbackStore.currentSlotIndex.
    //
    // BUG observado por Renato em campo: o player ficava em loop 1→5→1
    // (toca 5 dos 8 vídeos e reseta pra início). Uma das causas: quando
    // `applyPlaylistFromPlaybackStore` é chamado (em sync ou mudança de
    // schedule), ele passa `playbackStore.currentSlotIndex` como
    // `startIndex` pro `setPlaylist`. Mas o `advanceSlot()` da
    // playbackStore NUNCA é chamado em produção (só em testes), então
    // o índice fica em 0 eternamente. Resultado: qualquer setPlaylist
    // com sameContent=false (ex: presigned URL refresh) reseta o
    // currentIndex pra 0.
    //
    // Fix: sempre que streamStore avança, playbackStore avança junto
    // (defensivo). Em Tier 4 (currentSlotSequence=null), advanceSlot
    // é no-op — sem efeito colateral. Em Tier 2/3, mantém os 2 índices
    // sincronizados, garantindo que o startIndex passado pra
    // setPlaylist em syncs subsequentes seja o correto.
    //
    // Multi-zone: advance by the correct number of slots (may be >1 when
    // skipping zone group siblings).
    const totalAdvance =
      nextIndex >= this.currentIndex
        ? nextIndex - this.currentIndex
        : this.playlist.length - this.currentIndex + nextIndex;
    for (let i = 0; i < Math.max(1, totalAdvance); i++) {
      playbackStore.advanceSlot();
    }
  }

  playPrevious(): void {
    if (this.playlist.length === 0) return;

    runInAction(() => {
      this.currentIndex =
        this.currentIndex === 0
          ? this.playlist.length - 1
          : this.currentIndex - 1;
      this.currentItemStartedAt = Date.now();
    });
  }

  setPlaying(value: boolean): void {
    this.isPlaying = value;
  }

  /** Embedded (D2): host escondeu/mostrou a View → congela/retoma o playback. */
  setHostHidden(value: boolean): void {
    this.isHostHidden = value;
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
  }

  unmute(): void {
    this.isMuted = false;
  }

  /**
   * Called when media playback ends. `version` e `endedMediaId` são capturados quando o conteúdo
   * COMEÇOU a tocar; se o fim não pertence mais à mídia atual, é obsoleto e é ignorado.
   * Vide {@link isStaleMediaEnd} pro critério (e pro deadlock que ele conserta).
   */
  handleMediaEnd(version?: number, endedMediaId?: string): void {
    if (
      isStaleMediaEnd({
        eventVersion: version,
        currentVersion: this.playlistVersion,
        endedMediaId,
        currentMediaId: this.currentMedia?.id ?? null,
      })
    ) {
      console.log(
        '[StreamStore] ⏭️ Ignoring stale handleMediaEnd from previous playlist',
        {
          eventVersion: version,
          currentVersion: this.playlistVersion,
          endedMediaId,
          currentMediaId: this.currentMedia?.id,
        },
      );
      return;
    }
    // Roda 1×/mídia tocada — DEBUG (operador não precisa ver no console).
    logger.debug('[StreamStore] Mídia terminou, avançando para próxima');
    this.playNext();
  }

  /**
   * O item atual NÃO carregou (imagem remota com erro, vídeo que não abre, URL que não é
   * mídia). Falha isolada → pula como sempre. Se a volta inteira já falhou sem nada tocar,
   * SEGURA a tela atual e só tenta de novo após o backoff do {@link FailureBrake} — em vez de
   * rodar a playlist em cascata a cada poucos ms.
   *
   * @returns `true` quando segurou (o chamador não deve esconder o último quadro bom).
   */
  handleMediaFailure(
    reason: string,
    version?: number,
    failedMediaId?: string,
  ): boolean {
    if (
      isStaleMediaEnd({
        eventVersion: version,
        currentVersion: this.playlistVersion,
        endedMediaId: failedMediaId,
        currentMediaId: this.currentMedia?.id ?? null,
      })
    ) {
      return false;
    }
    const media = this.currentMedia;
    if (!media) return false;

    if (this.failureHold) {
      // Já segurando este item: a próxima tentativa já está marcada, não empilha outra.
      if (
        this.failureHold.mediaId === media.id &&
        this.failureHold.version === this.playlistVersion
      ) {
        return true;
      }
      this.clearFailureHold();
    }

    const decision = this.failureBrake.recordFailure({
      mediaId: media.id,
      playlistIds: this.playlist.map((m) => m.id),
      playlistVersion: this.playlistVersion,
    });
    if (decision.action === 'skip') {
      this.playNext();
      return false;
    }

    logger.warn(
      `[StreamStore] 🛑 Freio de cascata: nenhum item da playlist carregou — segurando a tela e tentando de novo em ${decision.delayMs} ms`,
      { reason, attempt: decision.attempt, mediaId: media.id.slice(0, 8) },
    );
    const mediaId = media.id;
    const heldVersion = this.playlistVersion;
    this.failureHold = {
      mediaId,
      version: heldVersion,
      timer: setTimeout(
        () => this.releaseFailureHold(mediaId, heldVersion),
        decision.delayMs,
      ),
    };
    return true;
  }

  /** Um item tocou de verdade (1º frame / imagem pintada): a cascata, se havia, acabou. */
  notePlaybackStarted(): void {
    if (this.failureBrake.isEngaged) {
      logger.info(
        '[StreamStore] ✅ Freio de cascata solto — item voltou a tocar',
      );
    }
    this.failureBrake.recordSuccess();
    this.clearFailureHold();
  }

  private releaseFailureHold(mediaId: string, version: number): void {
    this.failureHold = null;
    // Outra coisa já trocou a tela (o item carregou do cache, chegou programação nova, o pulso
    // de sync pulou): não há o que soltar.
    if (this.playlistVersion !== version || this.currentMedia?.id !== mediaId) {
      return;
    }
    // Grupo exclusivo: o relógio do grupo andou enquanto segurávamos — reentra onde ele ESTÁ.
    if (playbackStore.canonicalEpoch != null && this.realignHandler) {
      this.realignHandler('failure-brake');
      if (this.currentMedia?.id !== mediaId) return;
    }
    this.playNext();
  }

  private clearFailureHold(): void {
    if (!this.failureHold) return;
    clearTimeout(this.failureHold.timer);
    this.failureHold = null;
  }

  handleError(errorMessage: string): void {
    logger.error('[StreamStore] Erro de playback:', errorMessage);

    runInAction(() => {
      this.error = errorMessage;
    });

    // Tenta próxima mídia após erro — pelo freio de cascata: este também é um item que não
    // carregou, e uma playlist inteira falhando por aqui (ex.: arquivos do cache ilegíveis)
    // tem que segurar a tela igual às outras falhas. Versão/id capturados agora: se a tela
    // já trocou nesses 3 s, o pulo é obsoleto e não derruba o item novo.
    const version = this.playlistVersion;
    const mediaId = this.currentMedia?.id;
    setTimeout(() => {
      runInAction(() => {
        this.error = null;
      });
      this.handleMediaFailure(`error:${errorMessage}`, version, mediaId);
    }, 3000);
  }

  reset(): void {
    console.log('[StreamStore] 🔄 reset() called - clearing playlist', {
      previousPlaylistLength: this.playlist.length,
      previousCurrentIndex: this.currentIndex,
      stackTrace: new Error().stack,
    });

    this.failureBrake.reset();
    this.clearFailureHold();

    runInAction(() => {
      this.playlist = [];
      this.currentIndex = 0;
      this.isPlaying = false;
      this.isLoading = true;
      this.error = null;
    });

    console.log('[StreamStore] ✅ reset() completed - playlist cleared');
  }
}

export default StreamStore;
